import React, { useState, useEffect } from "react";
import { users, resolveAssetUrl } from "../utils/api";
import { useToast } from "../contexts/ToastContext";
import { useAdminSettings } from "../contexts/AdminSettingsContext";
import ConfirmationModal from "../components/ConfirmationModal";
import PaginationControls from "../components/PaginationControls";

const PAGE_SIZE = 10;

const UserManagementPage = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sendingResetFor, setSendingResetFor] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [managingUser, setManagingUser] = useState(null);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ password: "", confirmPassword: "" });
  const [resettingPassword, setResettingPassword] = useState(false);
  const { showInactiveStudents, toggleShowInactiveStudents } = useAdminSettings();
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null,
    userId: null,
    userName: "",
    userEmail: "",
  });
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await users.getAll();
      setAllUsers(response.data);
    } catch (error) {
      showError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setManagingUser(null);
    setEditingUser(user.id);
    setFormData({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      phone: user.phone,
      whatsapp: user.whatsapp,
      roomNumber: user.room_number || "",
      role: user.role,
      isMonitor: user.is_monitor,
      isApproved: user.is_approved,
      isActive: user.is_active,
    });
  };

  const handleSave = async () => {
    try {
      await users.update(editingUser, formData);
      showSuccess("User updated successfully");
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      showError("Failed to update user");
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await users.toggleActive(userId);
      showSuccess(response.data.message);
      setManagingUser(null);
      fetchUsers();
    } catch (error) {
      showError("Failed to toggle user active status");
    }
  };

  const handleDelete = (userId) => {
    const user = allUsers.find((u) => u.id === userId);
    const userName = user
      ? `${user.first_name} ${user.last_name}`
      : "this user";

    setConfirmModal({
      isOpen: true,
      action: "delete",
      userId,
      userName,
      userEmail: user?.email || "",
    });
    setManagingUser(null);
  };

  const handleSendPasswordReset = (user) => {
    setConfirmModal({
      isOpen: true,
      action: "resetEmail",
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      userEmail: user.email,
    });
    setManagingUser(null);
  };

  const handleDirectPasswordReset = (user) => {
    setManagingUser(null);
    setPasswordResetUser(user);
    setPasswordForm({ password: "", confirmPassword: "" });
  };

  const submitDirectPasswordReset = async (event) => {
    event.preventDefault();
    if (passwordForm.password !== passwordForm.confirmPassword) {
      showError("Passwords do not match");
      return;
    }
    if (
      passwordForm.password.length < 8 ||
      !/[a-z]/.test(passwordForm.password) ||
      !/[A-Z]/.test(passwordForm.password) ||
      !/\d/.test(passwordForm.password)
    ) {
      showError("Password must be at least 8 characters with uppercase, lowercase, and a number");
      return;
    }
    setResettingPassword(true);
    try {
      const response = await users.resetPassword(
        passwordResetUser.id,
        passwordForm.password
      );
      showSuccess(response.data.message);
      setPasswordResetUser(null);
      setPasswordForm({ password: "", confirmPassword: "" });
    } catch (error) {
      showError(error.response?.data?.error || "Failed to update password");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleConfirmDelete = async () => {
    const { userId } = confirmModal;

    try {
      await users.delete(userId);
      showSuccess("User deleted successfully");
      fetchUsers();
    } catch (error) {
      showError("Failed to delete user");
    }
  };

  const handleConfirmPasswordReset = async () => {
    const { userId } = confirmModal;
    setSendingResetFor(userId);

    try {
      const response = await users.sendPasswordReset(userId);
      showSuccess(response.data.message);
    } catch (error) {
      showError(
        error.response?.data?.error || "Failed to send password reset email"
      );
    } finally {
      setSendingResetFor(null);
    }
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      action: null,
      userId: null,
      userName: "",
      userEmail: "",
    });
  };

  const handleCancel = () => {
    setEditingUser(null);
    setFormData({});
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredUsers = allUsers.filter((user) => {
    if (!showInactiveStudents && !user.is_active) return false;
    if (roleFilter && user.role !== roleFilter) return false;
    if (!normalizedSearch) return true;
    return [
      `${user.first_name} ${user.last_name}`,
      user.email,
      user.phone,
    ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
  });
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [showInactiveStudents, searchQuery, roleFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 mt-16">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-bold">User Management</h1>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactiveStudents}
                  onChange={toggleShowInactiveStudents}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Show Inactive Students (Affects All Pages)
              </label>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search users
              </label>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by name, email, or phone number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All roles</option>
                <option value="student">Students</option>
                <option value="poshak_leader">Poshak Leaders</option>
                <option value="admin">Admins</option>
              </select>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Showing {filteredUsers.length} of {allUsers.length} users
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    WhatsApp
                  </th>
                  <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Room
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monitor
                  </th>
                  <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Approved
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user, index) => (
                  <tr key={user.id} className={!user.is_active ? 'bg-red-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(currentPage - 1) * PAGE_SIZE + index + 1}
                    </td>
                    {editingUser === user.id ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <input
                              type="text"
                              value={formData.firstName}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  firstName: e.target.value,
                                })
                              }
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <input
                              type="text"
                              value={formData.lastName}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  lastName: e.target.value,
                                })
                              }
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                email: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                phone: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap">
                          <input
                            type="tel"
                            value={formData.whatsapp}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                whatsapp: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            value={formData.roomNumber}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                roomNumber: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={formData.role}
                            onChange={(e) =>
                              setFormData({ ...formData, role: e.target.value })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="student">Student</option>
                            <option value="poshak_leader">Poshak Leader</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={formData.isMonitor}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                isMonitor: e.target.checked,
                              })
                            }
                          />
                        </td>
                        <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={formData.isApproved}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                isApproved: e.target.checked,
                              })
                            }
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                isActive: e.target.checked,
                              })
                            }
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={handleSave}
                            className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancel}
                            className="bg-gray-600 text-white px-3 py-1 rounded text-xs hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-3">
                            {user.profile_picture_url ? (
                              <img
                                src={resolveAssetUrl(user.profile_picture_url)}
                                alt=""
                                className="w-9 h-9 rounded-full object-cover border"
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold">
                                {user.first_name?.[0] || "U"}
                              </div>
                            )}
                            <span>{user.first_name} {user.last_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.phone}
                        </td>
                        <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.whatsapp}
                        </td>
                        <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.room_number || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              user.role === "admin"
                                ? "bg-red-100 text-red-800"
                                : user.role === "poshak_leader"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {user.role.replace("_", " ")}
                          </span>
                        </td>
                        <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              user.is_monitor
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {user.is_monitor ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              user.is_approved
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {user.is_approved ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              user.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            type="button"
                            onClick={() => setManagingUser(user)}
                            className="px-3 py-2 border border-blue-600 text-blue-700 rounded-md hover:bg-blue-50 font-medium"
                          >
                            Manage
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {paginatedUsers.length === 0 && (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center text-gray-500">
                      No users match the current search and filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredUsers.length}
            pageSize={PAGE_SIZE}
            itemLabel="users"
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {managingUser && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
          onClick={() => setManagingUser(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-5 border-b flex items-center gap-3">
              {managingUser.profile_picture_url ? (
                <img
                  src={resolveAssetUrl(managingUser.profile_picture_url)}
                  alt=""
                  className="w-12 h-12 rounded-full object-cover border"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-lg font-semibold">
                  {managingUser.first_name?.[0] || "U"}
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold">
                  {managingUser.first_name} {managingUser.last_name}
                </h2>
                <p className="text-sm text-gray-500">{managingUser.email}</p>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleEdit(managingUser)}
                className="px-4 py-3 text-left border rounded-md hover:bg-blue-50 hover:border-blue-300"
              >
                <span className="block font-medium text-blue-700">Edit user</span>
                <span className="text-xs text-gray-500">Details, role, and status</span>
              </button>
              <button
                type="button"
                onClick={() => handleToggleActive(managingUser.id)}
                className="px-4 py-3 text-left border rounded-md hover:bg-gray-50"
              >
                <span className="block font-medium">
                  {managingUser.is_active ? "Deactivate user" : "Activate user"}
                </span>
                <span className="text-xs text-gray-500">Change login access</span>
              </button>
              <button
                type="button"
                onClick={() => handleSendPasswordReset(managingUser)}
                disabled={sendingResetFor !== null}
                className="px-4 py-3 text-left border rounded-md hover:bg-purple-50 hover:border-purple-300 disabled:opacity-50"
              >
                <span className="block font-medium text-purple-700">Email reset link</span>
                <span className="text-xs text-gray-500">User chooses a new password</span>
              </button>
              <button
                type="button"
                onClick={() => handleDirectPasswordReset(managingUser)}
                className="px-4 py-3 text-left border rounded-md hover:bg-amber-50 hover:border-amber-300"
              >
                <span className="block font-medium text-amber-700">Set new password</span>
                <span className="text-xs text-gray-500">Admin enters it directly</span>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(managingUser.id)}
                className="sm:col-span-2 px-4 py-3 text-left border border-red-200 rounded-md hover:bg-red-50"
              >
                <span className="block font-medium text-red-700">Delete user</span>
                <span className="text-xs text-gray-500">Permanently remove user data</span>
              </button>
            </div>
            <div className="px-5 py-4 border-t flex justify-end">
              <button
                type="button"
                onClick={() => setManagingUser(null)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordResetUser && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <form
            onSubmit={submitDirectPasswordReset}
            className="bg-white rounded-lg shadow-xl w-full max-w-md"
          >
            <div className="p-5 border-b">
              <h2 className="text-lg font-semibold">Set New Password</h2>
              <p className="text-sm text-gray-600 mt-1">
                {passwordResetUser.first_name} {passwordResetUser.last_name} · {passwordResetUser.email}
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded p-3">
                The new password takes effect immediately. Any existing email reset links will be invalidated.
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={passwordForm.password}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, password: event.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  At least 8 characters with uppercase, lowercase, and a number.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={passwordForm.confirmPassword}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPasswordResetUser(null)}
                disabled={resettingPassword}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={resettingPassword}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
              >
                {resettingPassword ? "Updating..." : "Set Password"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={
          confirmModal.action === "resetEmail"
            ? handleConfirmPasswordReset
            : handleConfirmDelete
        }
        title={
          confirmModal.action === "resetEmail"
            ? "Send Password Reset Email"
            : "Delete User"
        }
        message={
          confirmModal.action === "resetEmail"
            ? `Send a password reset link to ${confirmModal.userName} at ${confirmModal.userEmail}? The link will expire in 30 minutes.`
            : `Are you sure you want to delete ${confirmModal.userName}? This action cannot be undone and will permanently remove all their data.`
        }
        confirmText={
          confirmModal.action === "resetEmail" ? "Send Email" : "Delete"
        }
        cancelText="Cancel"
        confirmStyle={
          confirmModal.action === "resetEmail"
            ? "bg-purple-600 hover:bg-purple-700 text-white"
            : "bg-red-600 hover:bg-red-700 text-white"
        }
        icon={confirmModal.action === "resetEmail" ? "✉️" : "👤"}
      />
    </div>
  );
};

export default UserManagementPage;
